// Shared styling constants for the agent module
import { Organization } from '../types/chatTypes';
import {
  Agriculture,
  Factory,
  LocalDining,
  Business,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

// Organization colors
export const ORGANIZATION_COLORS = {
  farm: DESIGN_TOKENS.colors.organization.farm,
  slaughterhouse: DESIGN_TOKENS.colors.organization.slaughterhouse,
  dairy: DESIGN_TOKENS.colors.organization.dairy,
  cooperative: DESIGN_TOKENS.colors.organization.cooperative,
  other: DESIGN_TOKENS.colors.organization.other,
} as const;

// Primary brand colors
export const BRAND_COLORS = {
  primary: DESIGN_TOKENS.colors.primary[500],
  primaryHover: DESIGN_TOKENS.colors.primary[600],
  primaryLight: DESIGN_TOKENS.colors.primary[50],
} as const;

// Neutral colors
export const NEUTRAL_COLORS = {
  gray50: DESIGN_TOKENS.colors.neutral[50],
  gray100: DESIGN_TOKENS.colors.neutral[100],
  gray200: DESIGN_TOKENS.colors.neutral[200],
  gray300: DESIGN_TOKENS.colors.neutral[300],
  gray400: DESIGN_TOKENS.colors.neutral[400],
  gray500: DESIGN_TOKENS.colors.neutral[500],
  gray600: DESIGN_TOKENS.colors.neutral[600],
  gray700: DESIGN_TOKENS.colors.neutral[700],
  gray800: DESIGN_TOKENS.colors.neutral[800],
  gray900: DESIGN_TOKENS.colors.neutral[900],
} as const;

// Status colors
export const STATUS_COLORS = {
  success: DESIGN_TOKENS.colors.status.success[500],
  warning: DESIGN_TOKENS.colors.status.warning[500],
  error: DESIGN_TOKENS.colors.status.error[500],
} as const;

// Common spacing values
export const SPACING = {
  xs: 0.5,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
} as const;

// Common border radius values
export const BORDER_RADIUS = {
  none: 0,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
  bubble: '18px',
  round: '20px',
  full: '50%',
} as const;

// Avatar sizes
export const AVATAR_SIZES = {
  sm: { width: 32, height: 32 },
  md: { width: 40, height: 40 },
  lg: { width: 48, height: 48 },
} as const;

// Helper functions
export const getOrganizationColor = (typeOrOrg: Organization['type'] | any) => {
  // Se é uma string, usar diretamente
  if (typeof typeOrOrg === 'string') {
    return ORGANIZATION_COLORS[typeOrOrg as keyof typeof ORGANIZATION_COLORS] || ORGANIZATION_COLORS.other;
  }
  // Se é um objeto (Farm), verificar se tem farm_type para identificar como fazenda
  if (typeOrOrg && (typeOrOrg.farm_type || typeOrOrg.type === 'farm' || typeOrOrg._key)) {
    return ORGANIZATION_COLORS.farm;
  }
  // Fallback para tipo específico ou other
  const type = typeOrOrg?.type || 'other';
  return ORGANIZATION_COLORS[type as keyof typeof ORGANIZATION_COLORS] || ORGANIZATION_COLORS.other;
};

export const getOrganizationIcon = (typeOrOrg: Organization['type'] | any) => {
  // Se é uma string, usar diretamente
  if (typeof typeOrOrg === 'string') {
    switch (typeOrOrg) {
      case 'farm':
        return Agriculture;
      case 'slaughterhouse':
        return Factory;
      case 'dairy':
        return LocalDining;
      case 'cooperative':
        return Business;
      default:
        return Business;
    }
  }
  // Se é um objeto (Farm), verificar se tem farm_type para identificar como fazenda
  if (typeOrOrg && (typeOrOrg.farm_type || typeOrOrg.type === 'farm' || typeOrOrg._key)) {
    return Agriculture;
  }
  // Fallback para tipo específico
  const type = typeOrOrg?.type || 'other';
  switch (type) {
    case 'farm':
      return Agriculture;
    case 'slaughterhouse':
      return Factory;
    case 'dairy':
      return LocalDining;
    case 'cooperative':
      return Business;
    default:
      return Business;
  }
};

export const getOrganizationTypeLabel = (typeOrOrg: Organization['type'] | any) => {
  // Se é uma string, usar diretamente
  if (typeof typeOrOrg === 'string') {
    switch (typeOrOrg) {
      case 'farm':
        return 'Fazenda';
      case 'slaughterhouse':
        return 'Frigorífico';
      case 'dairy':
        return 'Laticínio';
      case 'cooperative':
        return 'Cooperativa';
      default:
        return 'Outro';
    }
  }
  // Se é um objeto (Farm), verificar se tem farm_type para identificar como fazenda
  if (typeOrOrg && (typeOrOrg.farm_type || typeOrOrg.type === 'farm' || typeOrOrg._key)) {
    return 'Fazenda';
  }
  // Fallback para tipo específico
  const type = typeOrOrg?.type || 'other';
  switch (type) {
    case 'farm':
      return 'Fazenda';
    case 'slaughterhouse':
      return 'Frigorífico';
    case 'dairy':
      return 'Laticínio';
    case 'cooperative':
      return 'Cooperativa';
    default:
      return 'Outro';
  }
};

// Common style objects
export const COMMON_STYLES = {
  // Hover effects
  hoverEffect: {
    '&:hover': {
      backgroundColor: BRAND_COLORS.primaryLight,
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.2s ease',
  },
  
  // Button styles
  primaryButton: {
    bgcolor: BRAND_COLORS.primary,
    '&:hover': {
      bgcolor: BRAND_COLORS.primaryHover,
    },
  },
  
  // Input styles
  inputField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: NEUTRAL_COLORS.gray200,
      },
      '&:hover fieldset': {
        borderColor: BRAND_COLORS.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: BRAND_COLORS.primary,
      },
    },
  },
  
  // Organization chip
  organizationChip: (orgColor: string) => ({
    bgcolor: `${orgColor}20`,
    color: orgColor,
    fontSize: '0.7rem',
    height: 20,
  }),
  
  // Message bubble base
  messageBubbleBase: {
    maxWidth: '70%',
    p: SPACING.md,
    position: 'relative',
  },
  
  // User message bubble
  userMessageBubble: {
    bgcolor: BRAND_COLORS.primary,
    color: 'white',
    borderRadius: `${BORDER_RADIUS.bubble} ${BORDER_RADIUS.bubble} 4px ${BORDER_RADIUS.bubble}`,
  },
  
  // Bot message bubble
  botMessageBubble: {
    bgcolor: NEUTRAL_COLORS.gray50,
    color: NEUTRAL_COLORS.gray800,
    maxWidth: '88%',
    minWidth: '160px',
    borderRadius: `${BORDER_RADIUS.bubble} ${BORDER_RADIUS.bubble} ${BORDER_RADIUS.bubble} 4px`,
  },
} as const;